import { toast } from "@/hooks/use-toast"
import { isElectron as isElectronEnv } from '../utils/electron';
import type { ElectronAPI } from '../types/electron';
import type { Patient } from '@/types';
import { getAuthStorage, setAuthStorage, clearAuthStorage } from '@/lib/authStorage';

// Declare the window.electron interface for Electron
declare global {
  interface Window {
    electron?: {
      auth: {
        login: (email: string, password: string) => Promise<any>;
        logout: () => Promise<void>;
        check: () => Promise<any>;
      };
      patients: {
        getAll: () => Promise<any[]>;
        getById: (id: string) => Promise<any>;
        create: (data: any) => Promise<any>;
        update: (id: string, data: any) => Promise<any>;
        delete: (id: string) => Promise<any>;
        search: (query: string) => Promise<any[]>;
        getRecent: (limit: number) => Promise<any[]>;
      };
      appointments: {
        getAll: () => Promise<any[]>;
        getById: (id: string) => Promise<any>;
        create: (data: any) => Promise<any>;
        update: (id: string, data: any) => Promise<any>;
        delete: (id: string) => Promise<any>;
        getAllAppointments: (tenant_id: string, clinic_id: string) => Promise<any[]>;
        getUpcoming: (limit: number, doctor_id?: string, clinic_id?: string) => Promise<any[]>;
      };
      prescriptions: {
        getAll: () => Promise<any[]>;
        create: (data: any) => Promise<any>;
      };
      templates: {
        getAll: (type: string, doctorId: string, clinicId: string) => Promise<any[]>;
        create: (data: any) => Promise<any>;
      };
      system: {
        getBackendPort: () => Promise<number>;
      };
    };
  }
}

// Enhanced error handling types
interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

// Create a more specific error class
class ApiRequestError extends Error {
  public status?: number;
  public code?: string;
  public details?: any;

  constructor(message: string, status?: number, code?: string, details?: any) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Dynamic backend URL - will be updated when app starts
let BASE_URL = import.meta.env.VITE_API_BASE_URL || 
  (import.meta.env.MODE === 'production' 
    ? '/api'
    : 'http://localhost:3001');
let portDiscoveryPromise: Promise<void> | null = null;

// Function to discover and update the backend port
async function discoverBackendPort(): Promise<void> {
  if (!isElectronEnv() || !window.electron?.system?.getBackendPort) {
    return; // Not in Electron or API not available
  }

  try {
    const port = await window.electron.system.getBackendPort();
    if (port && port !== 3001) {
      BASE_URL = `http://localhost:${port}`;
      console.log(`🔗 Updated API base URL to: ${BASE_URL}`);
    }
  } catch (error: any) {
    console.warn("Could not get backend port from Electron, using default:", error);
  }
}

// Lazy initialization of backend port discovery
function ensureBackendPortDiscovered(): Promise<void> {
  if (!portDiscoveryPromise) {
    portDiscoveryPromise = discoverBackendPort();
  }
  return portDiscoveryPromise;
}

// Helper to determine if we should use Electron IPC
function shouldUseElectronAPI(endpoint: string): boolean {
  if (!isElectronEnv() || !window.electron) return false;
  
  // Map HTTP endpoints to Electron IPC endpoints
  const endpointMap: Record<string, string> = {
    '/patients': 'patients.getAll',
    '/patients/search': 'patients.search',
    '/appointments': 'appointments.getAll',
    '/prescriptions': 'prescriptions.getAll',
    '/templates': 'templates.getAll'
  };

  return !!endpointMap[endpoint];
}

// Helper to get the Electron IPC method
function getElectronMethod(endpoint: string, method: string = 'GET'): string | null {
  if (!isElectronEnv() || !window.electron) return null;

  // Map HTTP endpoints and methods to Electron IPC methods
  const methodMap: Record<string, Record<string, string>> = {
    '/patients': { 
      GET: 'patients.getAll',
      POST: 'patients.create'
    },
    '/patients/search': { GET: 'patients.search' },
    '/appointments': {
      GET: 'appointments.getAll',
      POST: 'appointments.create'
    },
    '/prescriptions': {
      GET: 'prescriptions.getAll',
      POST: 'prescriptions.create'
    },
    '/templates': {
      GET: 'templates.getAll',
      POST: 'templates.create'
    }
  };

  return methodMap[endpoint]?.[method] || null;
}

// Add error handling for CORS and CSP issues
const handleFetchError = (error: any, customError: string) => {
  console.error('API request failed:', error);
  
  if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
    const isCORSError = error.message.includes('CORS') || error.message.includes('Content Security Policy');
    if (isCORSError) {
      throw new ApiRequestError(
        'Unable to connect to server due to security policy. Please check your network connection or contact support.',
        0,
        'CORS_ERROR'
      );
    }
  }
  
  // Provide user-friendly message for server errors
  let userMessage = error.message || customError;
  if (error.status === 500 || error.status === 502 || error.status === 503 || error.status === 504) {
    userMessage = 'Server error. Please try again in a moment.';
  } else if (error.status === 0 || error.message === 'Failed to fetch') {
    userMessage = 'Network error. Please check your connection.';
  }
  
  throw new ApiRequestError(
    userMessage,
    error.status,
    error.code,
    error.details
  );
};

// Helper to get auth token (from session or local storage per "Remember me")
function getAuthToken(): string | null {
  try {
    return getAuthStorage().getItem('accessToken');
  } catch {
    return null;
  }
}

// Helper for safe fetch with enhanced error handling
export async function safeFetch<T>(
  url: string, 
  options?: RequestInit, 
  retries = 1,
  customError = "Something went wrong"
): Promise<T | null> { 
  // Extract the endpoint from the URL
  const endpoint = url.replace(BASE_URL, '');
  
  // Check if we should use Electron IPC
  if (shouldUseElectronAPI(endpoint)) {
    try {
      const method = getElectronMethod(endpoint, options?.method || 'GET');
      if (method) {
        const [namespace, func] = method.split('.');
        const data = options?.body ? JSON.parse(options.body as string) : undefined;
        // @ts-ignore - Dynamic access
        const result = await window.electron[namespace][func](data);
        return result as T;
      }
    } catch (error: any) {
      handleFetchError(error, customError);
    }
  }

  // Fallback to HTTP request
  await ensureBackendPortDiscovered();
  
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const timeoutMs = Number(import.meta.env.VITE_API_REQUEST_TIMEOUT_MS) || 30000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      // Get auth token and add to headers
      const token = getAuthToken();
      const authHeaders: Record<string, string> = {};
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }
      
      const fetchOptions: RequestInit = {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...authHeaders,
          ...options?.headers,
        }
      };

      const res = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      if (res.status === 401) {
        const isRefreshEndpoint = url.includes('/auth/refresh');
        const isLoginEndpoint = url.includes('/auth/login');
        
        // Don't auto-redirect on login failures; let the component handle the error
        if (isLoginEndpoint) {
          // Let it fall through to error handling below
        } else if (!isRefreshEndpoint && (await refreshAccessToken())) {
          const newToken = getAuthToken();
          if (newToken) {
            const retryOptions: RequestInit = {
              ...options,
              headers: {
                ...options?.headers,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                Authorization: `Bearer ${newToken}`,
              },
            };
            const retryRes = await fetch(url, retryOptions);
            if (retryRes.ok) {
              const contentType = retryRes.headers.get("Content-Type") || "";
              if (contentType.includes("application/json")) {
                return await retryRes.json();
              }
            }
          }
        } else if (!isLoginEndpoint) {
          // Only redirect to login for non-login endpoints (session expired, etc)
          clearAuthStorage();
          window.location.href = '/login';
          return null;
        }
      }

      if (!res.ok) {
        let errorMessage = `HTTP ${res.status}: ${res.statusText}`;
        let errorDetails: any = null;

        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          errorDetails = errorData;
        } catch {
          // If response isn't JSON, use status text
        }

        throw new ApiRequestError(errorMessage, res.status, 'HTTP_ERROR', errorDetails);
      }
      
      const contentType = res.headers.get("Content-Type") || "";
      if (!contentType.includes("application/json")) {
        throw new ApiRequestError("Server returned invalid response format", 502, 'INVALID_RESPONSE');
      }
      
      const data = await res.json();
      return data;
      
    } catch (err: any) {
      lastError = err;
      
      if (err instanceof ApiRequestError) {
        if (err.status && [400, 401, 403, 404, 422].includes(err.status)) {
          break; // Don't retry client errors
        }
      }
      
      if (attempt === retries) {
        break;
      }
      
      const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  if (lastError) {
    handleFetchError(lastError, customError);
  }
  
  return null;
}

// ------------------ Patients ------------------

export async function getPatients(options?: {
  tenant_id?: string;
  clinic_id: string;
  search?: string;
  gender?: string;
  sortField?: 'name' | 'age' | 'created_at';
  sortOrder?: 'asc' | 'desc';
  page?: number | string;
  pageSize?: number | string;
}): Promise<{ patients: Patient[]; total: number }> {
  if (isElectronEnv() && window.electron?.patients?.getAll) {
    try {
      console.log('Getting patients via IPC...');
      const data = await window.electron.patients.getAll();
      console.log('Got patients via IPC');
      return { patients: data, total: data.length };
    } catch (error: any) {
      console.error('Error getting patients via IPC:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to get patients",
        variant: "destructive",
      });
      throw error;
    }
  }

  // clinic_id is required; doctor_id is optional (receptionists don't have one)
  if (!options || !options.clinic_id) {
    throw new Error('clinic_id is required');
  }

  const params = new URLSearchParams();
  params.append('clinic_id', options.clinic_id);
  if (options.search) params.append('search', options.search);
  if (options.gender) params.append('gender', options.gender);
  if (options.sortField) params.append('sortField', options.sortField);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);
  if (options.page) params.append('page', String(options.page));
  if (options.pageSize) params.append('pageSize', String(options.pageSize));

  const response = await safeFetch<{ patients: Patient[]; total: number }>(`${BASE_URL}/patients?${params.toString()}`);
  if (response && Array.isArray(response.patients) && typeof response.total === 'number') {
    return response;
  }
  return { patients: [], total: 0 };
}

export async function getRecentPatients(limit = 5, doctor_id?: string, clinic_id?: string) {
  if (isElectronEnv() && window.electron?.patients?.getRecent) {
    return window.electron.patients.getRecent(limit);
  }
  
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('sort', 'desc');
  if (doctor_id) params.append('doctor_id', doctor_id);
  if (clinic_id) params.append('clinic_id', clinic_id);
  
  const response = await safeFetch<{ patients: any[]; total: number }>(`${BASE_URL}/patients?${params.toString()}`);
  
  // Handle the response structure: { patients: Patient[], total: number }
  if (response && response.patients && Array.isArray(response.patients)) {
    return response.patients;
  }
  
  // Fallback for direct array response
  if (response && Array.isArray(response)) {
    return response;
  }
  
  return [];
}

export async function searchPatients(query: string, clinic_id?: string) {
  if (isElectronEnv() && window.electron?.patients?.search) {
    return window.electron.patients.search(query);
  }
  const params = new URLSearchParams({ q: query });
  if (clinic_id) params.append('clinic_id', clinic_id);
  return safeFetch<any[]>(`${BASE_URL}/patients/search?${params.toString()}`);
}

export async function addPatient(data: {
  name: string
  age: number
  gender: string
  phone: string
}) {
  if (isElectronEnv() && window.electron?.patients?.create) {
    return window.electron.patients.create(data);
  }
  return safeFetch<any>(`${BASE_URL}/patients`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function updatePatient(patientId: string, data: {
  name?: string
  age?: number
  gender?: string
  phone?: string
}) {
  return safeFetch<any>(`${BASE_URL}/patients/${patientId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function getProfile(patientId: string) {
  return safeFetch<any>(`${BASE_URL}/patients/profile/${patientId}`)
}

export async function getPatient(patientId: string) {
  return safeFetch<any>(`${BASE_URL}/patients/${patientId}`)
}

// ------------------ Prescriptions ------------------

export async function getPrescriptions() {
  if (isElectronEnv() && window.electron?.prescriptions?.getAll) {
    return window.electron.prescriptions.getAll();
  }
  return safeFetch<any[]>(`${BASE_URL}/prescriptions`);
}

export async function addPrescription(data: any) {
  if (isElectronEnv() && window.electron?.prescriptions?.create) {
    return window.electron.prescriptions.create(data);
  }
  return safeFetch<any>(`${BASE_URL}/prescriptions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function updatePrescription(data: any) {
  const { id, ...body } = data; // Extract id for URL, remove from body
  return safeFetch<any>(`${BASE_URL}/prescriptions/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
}

export async function getPrescriptionsByPatient(patientId: string) {
  return safeFetch<any[]>(`${BASE_URL}/prescriptions/patient/${patientId}`)
}

export async function getPrescriptionById(prescriptionId: string) {
  return safeFetch<any>(`${BASE_URL}/prescriptions/${prescriptionId}`)
}

// ------------------ Appointments ------------------

export async function getAppointments() {
  if (isElectronEnv() && window.electron?.appointments?.getAll) {
    return window.electron.appointments.getAll();
  }
  return safeFetch<any[]>(`${BASE_URL}/appointments`);
}

export async function getUpcomingAppointments(limit = 5, doctor_id?: string, clinic_id?: string) {
  if (isElectronEnv() && window.electron?.appointments?.getUpcoming) {
    return window.electron.appointments.getUpcoming(limit);
  }
  
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  if (doctor_id) params.append('doctor_id', doctor_id);
  if (clinic_id) params.append('clinic_id', clinic_id);
  
  return safeFetch<any[]>(`${BASE_URL}/appointments/upcoming?${params.toString()}`)
}

export async function scheduleAppointment(appt: any) {
  return safeFetch<any>(`${BASE_URL}/appointments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(appt),
  })
}

export async function addAppointment(data: any) {
  if (isElectronEnv() && window.electron?.appointments?.create) {
    return window.electron.appointments.create(data);
  }
  return safeFetch<any>(`${BASE_URL}/appointments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function updateAppointment(data: {
  id?: string
  patient_id?: string
  date?: string
  time?: string
  reason?: string
  status?: string
  updated_at?: string
}) {
  return safeFetch<any>(`${BASE_URL}/appointments/${data.id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  })
}

export async function deleteAppointment(appointmentId: string) {
  return safeFetch<any>(`${BASE_URL}/appointments/${appointmentId}`, {
    method: "DELETE",
  })
}

export async function getAllAppointments(tenant_id: string, clinic_id: string) {
  if (isElectronEnv() && window.electron?.appointments?.getAllAppointments) {
    try {
      console.log('Getting all appointments via IPC...');
      const data = await window.electron.appointments.getAllAppointments(tenant_id, clinic_id);
      console.log('Got all appointments via IPC:', data);
      return data;
    } catch (error: any) {
      console.error('Error getting all appointments via IPC:', error);
      throw new Error(error.message || "Failed to get all appointments");
    }
  }
  return safeFetch<any[]>(`${BASE_URL}/appointments/all`);
}

// ------------------ Queue ------------------

export interface QueueEntry {
  id: string;
  tokenNumber: number;
  sortOrder: number;
  patientName: string;
  patientPhone: string | null;
  patientId: string | null;
  appointmentId: string | null;
  status: 'waiting' | 'in_consultation' | 'completed' | 'cancelled';
  isPriority: boolean;
  checkedInAt: string;
  consultationStartAt: string | null;
  consultationEndAt: string | null;
  waitingTimeMinutes?: number | null;
}

export interface QueueTodayResponse {
  doctorId: string;
  clinicId: string;
  queueDate: string;
  summary: { totalWaiting: number; totalInConsultation: number; totalCompleted: number; totalCancelled: number };
  entries: QueueEntry[];
}

export async function getQueueToday(doctorId: string, clinicId: string) {
  const params = new URLSearchParams({ doctorId, clinicId });
  return safeFetch<QueueTodayResponse>(`${BASE_URL}/queue/today?${params.toString()}`);
}

export async function queueCheckin(appointmentId: string) {
  return safeFetch<QueueEntry>(`${BASE_URL}/queue/checkin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ appointmentId }),
  });
}

export async function queueWalkin(
  patientId: string,
  doctorId: string,
  clinicId: string,
  isPriority: boolean = false
): Promise<QueueEntry> {
  return safeFetch<QueueEntry>(`${BASE_URL}/queue/walkin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ patientId, doctorId, clinicId, isPriority }),
  });
}

export async function queueWalkinRegister(data: {
  name: string;
  phone: string;
  age?: number;
  gender?: string;
  doctorId: string;
  clinicId: string;
  isPriority?: boolean;
}): Promise<{ queueEntry: QueueEntry; patientCreated: boolean; patientId: string }> {
  return safeFetch<{ queueEntry: QueueEntry; patientCreated: boolean; patientId: string }>(
    `${BASE_URL}/queue/walkin/register`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
}

export interface QueueMyResponse {
  doctorId: string;
  clinicId: string;
  queueDate: string;
  summary: { totalWaiting: number; totalInConsultation: number };
  entries: QueueEntry[];
}

export async function getQueueMy(date?: string) {
  const params = date ? `?date=${encodeURIComponent(date)}` : '';
  return safeFetch<QueueMyResponse>(`${BASE_URL}/queue/my${params}`);
}

export async function queueStartConsultation(queueId: string) {
  return safeFetch<QueueEntry>(`${BASE_URL}/queue/${queueId}/start`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  });
}

export async function queueCompleteConsultation(queueId: string) {
  return safeFetch<QueueEntry>(`${BASE_URL}/queue/${queueId}/complete`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  });
}

/** Toggle priority: true = move to front, false = move to end. Returns updated full queue. */
export async function queueReorder(queueId: string, isPriority: boolean) {
  return safeFetch<QueueTodayResponse>(`${BASE_URL}/queue/${queueId}/reorder`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isPriority }),
  });
}

/** Mark patient as left (cancel from queue). Only waiting entries. */
export async function queueCancel(queueId: string) {
  return safeFetch<QueueEntry>(`${BASE_URL}/queue/${queueId}/cancel`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
  });
}

// ------------------ Invoices ------------------

// Legacy invoice functions removed — use getBillingInvoices, createBillingInvoice, getPatientInvoices

// ------------------ Templates ------------------

// Get templates with enhanced filtering and search
export async function getTemplateSections(
  type: string, 
  doctor_id: string, 
  clinic_id: string,
  options?: {
    search?: string;
    category?: 'personal' | 'clinic' | 'shared';
    favorites_only?: boolean;
    limit?: number;
    offset?: number;
  }
) {
  const params = new URLSearchParams({
    type: encodeURIComponent(type),
    doctor_id: encodeURIComponent(doctor_id),
    clinic_id: encodeURIComponent(clinic_id)
  });

  // Add optional parameters
  if (options?.search) params.append('search', options.search);
  if (options?.category) params.append('category', options.category);
  if (options?.favorites_only) params.append('favorites_only', 'true');
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());

  
  return safeFetch<any>(`${BASE_URL}/templates?${params.toString()}`);
}

// Get all templates for management page
export async function getAllTemplates(
  doctor_id: string, 
  clinic_id: string,
  options?: {
    search?: string;
    type?: string;
    category?: string;
    favorites_only?: boolean;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_direction?: string;
    tags?: string[];
  }
) {
  const params = new URLSearchParams({
    doctor_id: encodeURIComponent(doctor_id),
    clinic_id: encodeURIComponent(clinic_id)
  });

  // Add optional parameters
  if (options?.search) params.append('search', options.search);
  if (options?.type) params.append('type', options.type);
  if (options?.category) params.append('category', options.category);
  if (options?.favorites_only) params.append('favorites_only', 'true');
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());
  // Note: sort_by and sort_direction are handled in frontend
  // tags filtering is also handled in frontend

  
  // Use the correct endpoint - /templates (not /templates/all)
  const response = await safeFetch(`${BASE_URL}/templates?${params.toString()}`);
  
  // The backend returns { templates: [...], pagination: {...} }
  // So we return the full response structure
  return response;
}

// Get template statistics for dashboard
export async function getTemplateStats(doctor_id: string, clinic_id: string) {
  
  return safeFetch<any>(`${BASE_URL}/templates/stats?doctor_id=${encodeURIComponent(doctor_id)}&clinic_id=${encodeURIComponent(clinic_id)}`);
}

// Create new template
export async function addTemplateSection(data: {
  id?: string;
  doctor_id: string;
  clinic_id: string;
  title: string;
  description?: string;
  type: 'complaint' | 'medicine' | 'investigation' | 'advice' | 'diagnosis' | 'quick_notes';
  value: any;
  tags?: string[] | string;
  category?: 'personal' | 'clinic' | 'shared';
  is_global?: boolean;
  is_favorite?: boolean;
  created_by?: string;
}) {
  
  return safeFetch<any>(`${BASE_URL}/templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data), // Single template, not array
  });
}

// Update existing template
export async function updateTemplateSection(
  templateId: string, 
  data: {
    doctor_id: string;
    title?: string;
    description?: string;
    type?: string;
    value?: any;
    tags?: string[] | string;
    category?: string;
    is_favorite?: boolean;
  }
) {
  
  return safeFetch<any>(`${BASE_URL}/templates/${templateId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

// Delete template (soft delete)
export async function deleteTemplateSection(templateId: string, doctor_id: string) {
  
  return safeFetch<any>(`${BASE_URL}/templates/${templateId}?doctor_id=${encodeURIComponent(doctor_id)}`, {
    method: "DELETE",
  });
}

// Toggle favorite status
export async function toggleTemplateFavorite(
  templateId: string, 
  doctor_id: string, 
  is_favorite: boolean
) {
  
  return safeFetch<any>(`${BASE_URL}/templates/${templateId}/favorite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ doctor_id, is_favorite }),
  });
}

// Track template usage
export async function trackTemplateUsage(
  templateId: string, 
  doctor_id: string,
  context?: string,
  patient_id?: string
) {
  
  return safeFetch<any>(`${BASE_URL}/templates/${templateId}/use`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ 
      doctor_id, 
      context: context || 'prescription_form',
      patient_id 
    }),
  });
}

// Batch create templates (keeping for backward compatibility)
export async function syncTemplateSections(batch: any[]) {
  return safeFetch<any>(`${BASE_URL}/templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(batch),
  });
}

// Duplicate template
export async function duplicateTemplate(
  templateId: string, 
  doctor_id: string, 
  newTitle?: string
) {
  
  // Get original template first
  const original = await safeFetch(`${BASE_URL}/templates?doctor_id=${doctor_id}&clinic_id=dummy`) as any;
  
  if (original?.templates) {
    const template = original.templates.find((t: any) => t.id === templateId);
    if (template) {
      return addTemplateSection({
        ...template,
        id: undefined, // Let server generate new ID
        title: newTitle || `${template.title} (Copy)`,
        usage_count: 0,
        is_favorite: false,
      });
    }
  }
  
  throw new Error('Template not found');
}

// Export templates (for backup)
export async function exportTemplates(doctor_id: string, clinic_id: string, options?: {
  type?: string;
  category?: string;
  favorites_only?: boolean;
}) {
  
  const response = await getAllTemplates(doctor_id, clinic_id, { limit: 1000, ...options }) as any;
  
  // Handle the response structure: { templates: [...], pagination: {...} }
  if (response?.templates) {
    const exportData = {
      version: "1.0",
      exported_at: new Date().toISOString(),
      doctor_id,
      clinic_id,
      templates: response.templates
    };
    
    // Create and download file
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `templates-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    return { success: true, count: response.templates.length };
  }
  
  throw new Error('Failed to export templates');
}

// Import templates (from backup)
export async function importTemplates(
  file: File, 
  doctor_id: string, 
  clinic_id: string
) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string;
        const importData = JSON.parse(content);
        
        if (!importData.templates || !Array.isArray(importData.templates)) {
          throw new Error('Invalid import file format');
        }
        
        // Process templates for import
        const templatesToImport = importData.templates.map((template: any) => ({
          ...template,
          id: undefined, // Generate new IDs
          doctor_id,
          clinic_id,
          usage_count: 0, // Reset usage count
          created_at: undefined, // Set new creation time
          updated_at: undefined,
        }));
        
        // Import in batches
        const results = [];
        for (const template of templatesToImport) {
          try {
            const result = await addTemplateSection(template);
            results.push(result);
          } catch (error) {
            console.warn('Failed to import template:', template.title, error);
          }
        }
        
        resolve({ 
          success: true, 
          imported: results.length, 
          total: templatesToImport.length 
        });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

// ------------------ Settings ------------------

// GET /settings - Get user settings
export async function getSettings(doctor_id: string, clinic_id: string) {
  const params = new URLSearchParams({
    doctor_id: encodeURIComponent(doctor_id),
    clinic_id: encodeURIComponent(clinic_id)
  });

  const settings = await safeFetch<any>(`${BASE_URL}/settings?${params.toString()}`);
  
  // Transform flat backend response to nested frontend structure
  if (settings) {
    return {
      ...settings,
      profile: {
        name: settings.profile_name || '',
        email: settings.profile_email || '',
        phone: settings.profile_phone || '',
        specialty: settings.profile_specialty || 'General Medicine',
        license: settings.profile_license || '',
        signature: settings.profile_signature || ''
      },
      clinic: {
        name: settings.clinic_name || '',
        address: settings.clinic_address || '',
        phone: settings.clinic_phone || '',
        email: settings.clinic_email || ''
      },
      preferences: {
        theme: settings.theme || 'light',
        language: settings.language || 'en',
        notifications: settings.notifications !== undefined ? settings.notifications : true,
        autoSave: settings.auto_save !== undefined ? settings.auto_save : true,
        printFormat: settings.print_format || 'a4'
      }
    };
  }
  
  // Always return all keys with defaults if settings is null
  return {
    profile: {
      name: '', email: '', phone: '', specialty: 'General Medicine', license: '', signature: ''
    },
    clinic: {
      name: '', address: '', phone: '', email: ''
    },
    preferences: {
      theme: 'light', language: 'en', notifications: true, autoSave: true, printFormat: 'a4'
    }
  };
}

// Update user settings (handles first-time creation)
export async function updateSettings(doctor_id: string, clinic_id: string, settings: any) {
  const data = {
    doctor_id,
    clinic_id,
    ...settings
  };

  
  return safeFetch<any>(`${BASE_URL}/settings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

// Get database statistics
export async function getDatabaseStats(doctor_id: string, clinic_id: string) {
  const params = new URLSearchParams({
    doctor_id: encodeURIComponent(doctor_id),
    clinic_id: encodeURIComponent(clinic_id)
  });

  
  return safeFetch<any>(`${BASE_URL}/settings/stats?${params.toString()}`);
}

export async function getBillingServices(clinicId: string, doctorId?: string, category?: string) {
  let url = `${BASE_URL}/billing/services/${clinicId}`;
  const params = new URLSearchParams();
  
  if (doctorId) params.append('doctorId', doctorId);
  if (category) params.append('category', category);
  
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return safeFetch(url);
}

export async function getBillingServiceById(serviceId: string) {
  return safeFetch<any>(`${BASE_URL}/billing/services/single/${serviceId}`);
}

export async function createBillingService(serviceData: {
  clinic_id: string;
  doctor_id?: string;
  name: string;
  price: number;
  category?: 'consultation' | 'procedure' | 'diagnostic' | 'medicine' | 'lab';
  is_active?: boolean;
  sort_order?: number;
}) {
  return safeFetch<any>(`${BASE_URL}/billing/services`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(serviceData),
  });
}

export async function updateBillingService(serviceId: string, serviceData: {
  name: string;
  price: number;
  category?: string;
  is_active?: boolean;
  sort_order?: number;
}) {
  return safeFetch<any>(`${BASE_URL}/billing/services/${serviceId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(serviceData),
  });
}

export async function deleteBillingService(serviceId: string) {
  return safeFetch<any>(`${BASE_URL}/billing/services/${serviceId}`, {
    method: "DELETE",
  });
}

export async function getBillingServiceStats(clinicId: string, doctorId?: string) {
  let url = `${BASE_URL}/billing/services/stats/${clinicId}`;
  if (doctorId) url += `?doctorId=${doctorId}`;
  
  return safeFetch(url);
}

export async function createDefaultBillingServices(clinicId: string, createdBy?: string) {
  return safeFetch<any>(`${BASE_URL}/billing/services/default/${clinicId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ created_by: createdBy }),
  });
}

// ------------------ Billing Invoices ------------------

// getBillingInvoices merged into getAllBillingInvoices below

export async function getBillingInvoicesByPatient(patientId: string) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/patient/${patientId}`);
}

export async function getBillingInvoiceById(invoiceId: string) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/single/${invoiceId}`);
}

export async function createBillingInvoice(invoiceData: {
  patient_id?: string;
  patient_name: string;
  patient_phone?: string;
  patient_address?: string;
  doctor_id: string;
  clinic_id: string;
  date?: string;
  due_date?: string;
  items: Array<{
    service_id?: string;
    service_name: string;
    category?: string;
    quantity: number;
    unit_price: number;
    total_price?: number;
    notes?: string;
  }>;
  discount_amount?: number;
  tax_amount?: number;
  payment_method?: 'Cash' | 'UPI' | 'Card' | 'Bank Transfer' | 'Cheque' | 'Insurance';
  payment_status?: 'Paid' | 'Pending' | 'Partial';
  paid_amount?: number;
  notes?: string;
}) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(invoiceData),
  });
}

export async function updateBillingInvoice(invoiceId: string, invoiceData: {
  patient_name: string;
  patient_phone?: string;
  patient_address?: string;
  date: string;
  due_date?: string;
  discount_amount?: number;
  tax_amount?: number;
  payment_method?: string;
  payment_status?: string;
  paid_amount?: number;
  notes?: string;
}) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/${invoiceId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(invoiceData),
  });
}

export async function recordInvoicePayment(invoiceId: string, paymentData: {
  amount: number;
  payment_method: string;
  payment_date?: string;
}) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/${invoiceId}/payment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(paymentData),
  });
}

export async function cancelBillingInvoice(invoiceId: string) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/${invoiceId}`, {
    method: "DELETE",
  });
}

export async function getBillingInvoiceStats(clinicId: string, doctorId?: string, dateFrom?: string, dateTo?: string) {
  let url = `${BASE_URL}/billing/invoices/stats/${clinicId}`;
  const params = new URLSearchParams();
  
  if (doctorId) params.append('doctor_id', doctorId);
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return safeFetch(url);
}

export async function getBillingRevenueSummary(clinicId: string, days: number = 30) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/revenue/${clinicId}?days=${days}`);
}

// Add these functions to your existing /lib/api.js file

export async function getAllBillingInvoices(clinicId: string, options: {
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  doctorId?: string;
} = {}) {
  let url = `${BASE_URL}/billing/invoices`;
  
  // Add query parameters if options provided
  const queryParams = new URLSearchParams();
  queryParams.append('clinic_id', clinicId);
  
  if (options.status && options.status !== 'all') {
    queryParams.append('payment_status', options.status);
  }
  if (options.dateFrom) {
    queryParams.append('date_from', options.dateFrom);
  }
  if (options.dateTo) {
    queryParams.append('date_to', options.dateTo);
  }
  if (options.doctorId) {
    queryParams.append('doctor_id', options.doctorId);
  }
  
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  return safeFetch(url);
}

export async function getBillingAnalytics(clinicId: string, options: {
  startDate?: string;
  endDate?: string;
  doctorId?: string;
} = {}) {
  let url = `${BASE_URL}/billing/invoices/stats/${clinicId}`;
  
  // Add optional parameters
  const queryParams = new URLSearchParams();
  if (options.startDate) {
    queryParams.append('date_from', options.startDate);
  }
  if (options.endDate) {
    queryParams.append('date_to', options.endDate);
  }
  if (options.doctorId) {
    queryParams.append('doctor_id', options.doctorId);
  }
  
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  return safeFetch(url);
}

export async function getBillingSummary(clinicId: string) {
  const url = `${BASE_URL}/billing/summary/${clinicId}`;
  
  return safeFetch(url);
}

export async function getInvoiceDetails(invoiceId: string) {
  const url = `${BASE_URL}/billing/invoices/${invoiceId}`;
  
  return safeFetch(url);
}

export async function exportBillingData(clinicId: string, format = 'csv', filters: {
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  doctorId?: string;
} = {}) {
  let url = `${BASE_URL}/billing/export/${clinicId}`;
  
  const queryParams = new URLSearchParams();
  queryParams.append('format', format);
  
  if (filters.dateFrom) {
    queryParams.append('dateFrom', filters.dateFrom);
  }
  if (filters.dateTo) {
    queryParams.append('dateTo', filters.dateTo);
  }
  if (filters.status && filters.status !== 'all') {
    queryParams.append('status', filters.status);
  }
  if (filters.doctorId) {
    queryParams.append('doctorId', filters.doctorId);
  }
  
  url += `?${queryParams.toString()}`;

  return safeFetch(url, {
    headers: {
      'Accept': format === 'csv' ? 'text/csv' : 'application/json'
    }
  });
}

export async function updateInvoiceStatus(invoiceId: string, status: string) {
  return safeFetch<any>(`${BASE_URL}/billing/invoices/${invoiceId}/status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
}

export async function getBillingDashboardStats(clinicId: string, doctorId?: string) {
  let url = `${BASE_URL}/billing/dashboard/${clinicId}`;
  if (doctorId) url += `?doctorId=${doctorId}`;
  
  return safeFetch(url);
}

export async function getMonthlyBillingTrends(clinicId: string, months = 12) {
  const url = `${BASE_URL}/billing/trends/${clinicId}?months=${months}`;
  
  return safeFetch(url);
}

export async function getTopBillingServices(clinicId: string, limit = 10) {
  const url = `${BASE_URL}/billing/services/top/${clinicId}?limit=${limit}`;
  
  return safeFetch(url);
}

export async function deleteInvoice(invoiceId: string) {
  const url = `${BASE_URL}/billing/invoices/${invoiceId}`;
  
  return safeFetch(url, {
    method: "DELETE"
  });
}

export async function duplicateInvoice(invoiceId: string) {
  const url = `${BASE_URL}/billing/invoices/${invoiceId}/duplicate`;
  
  return safeFetch(url, {
    method: "POST"
  });
}

export async function getDoctorInfo(doctor_id: string, clinic_id: string) {
  const params = new URLSearchParams({
    doctor_id: encodeURIComponent(doctor_id),
    clinic_id: encodeURIComponent(clinic_id)
  });
  const settings = await safeFetch<any>(`${BASE_URL}/settings?${params.toString()}`);
  
  // Return doctor profile structure
  if (settings) {
    return {
      name: settings.profile_name,
      email: settings.profile_email,
      phone: settings.profile_phone,
      specialty: settings.profile_specialty,
      license: settings.profile_license,
      signature: settings.profile_signature
    };
  }
  
  return null;
}

export async function getClinicInfo(clinic_id: string, doctor_id: string) {
  const params = new URLSearchParams({
    clinic_id: encodeURIComponent(clinic_id),
    doctor_id: encodeURIComponent(doctor_id)
  });
  const settings = await safeFetch<any>(`${BASE_URL}/settings?${params.toString()}`);
  
  // Return clinic structure
  if (settings) {
    return {
      name: settings.clinic_name,
      address: settings.clinic_address,
      phone: settings.clinic_phone,
      email: settings.clinic_email
    };
  }
  
  return null;
}

// ==================================================
// DOCTORS
// ==================================================

export async function getDoctors() {
  return safeFetch<any[]>(`${BASE_URL}/doctors`)
}

export async function getDoctor(id: string) {
  return safeFetch<any>(`${BASE_URL}/doctors/${id}`)
}

// ==================================================
// CLINICS
// ==================================================

export async function getClinics() {
  return safeFetch<any[]>(`${BASE_URL}/clinics`)
}

export async function getClinic(id: string) {
  return safeFetch<any>(`${BASE_URL}/clinics/${id}`)
}

// ------------------ Auth ------------------

export async function loginUser(email: string, password: string, rememberMe = true) {
  const response = await safeFetch<{
    user: any;
    accessToken: string;
    refreshToken: string;
    expiresIn: string;
  }>(`${BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password })
  });

  if (response && response.accessToken) {
    try {
      setAuthStorage(rememberMe);
      const store = getAuthStorage();
      store.setItem('accessToken', response.accessToken);
      store.setItem('refreshToken', response.refreshToken);
    } catch (error) {
      console.error('Failed to store tokens:', error);
    }
  }

  return response;
}

// Refresh access token
export async function refreshAccessToken(): Promise<boolean> {
  try {
    const store = getAuthStorage();
    const refreshToken = store.getItem('refreshToken');
    if (!refreshToken) return false;

    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      store.setItem('accessToken', data.accessToken);
      if (data.refreshToken) store.setItem('refreshToken', data.refreshToken);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
}

// Logout and clear tokens
export function logoutUser() {
  clearAuthStorage();
}

// Request password reset (sends no email; backend returns generic message)
export async function requestPasswordReset(email: string) {
  return safeFetch<{ message: string }>(`${BASE_URL}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

// Submit sign-up / contact request (full name, phone, optional email)
export async function submitSignupRequest(data: { full_name: string; phone: string; email?: string }) {
  return safeFetch<{ message: string; id: string }>(`${BASE_URL}/auth/signup-request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

// Reset password with token from email/link
export async function resetPassword(token: string, newPassword: string) {
  return safeFetch<{ message: string }>(`${BASE_URL}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

// ==================================================
// DRUGS
// ==================================================

export interface DrugSearchResult {
  id: string;
  brand_name: string;
  generic_name: string;
  strength: string;
  form: string;
  category: string;
  schedule: string;
  dosage: string | null;
  taken_when: string | null;
  duration: string | null;
  default_notes: string | null;
  is_favourite: boolean;
  usage_count: number;
}

export async function searchDrugs(query: string, doctorId: string): Promise<DrugSearchResult[]> {
  const response = await safeFetch<DrugSearchResult[]>(
    `${BASE_URL}/drugs/search?q=${encodeURIComponent(query)}&doctorId=${encodeURIComponent(doctorId)}`
  );
  return response || [];
}

export async function recordDrugUsage(drugId: string, doctorId: string, tenantId: string): Promise<void> {
  try {
    await safeFetch<void>(`${BASE_URL}/drugs/${encodeURIComponent(drugId)}/usage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ doctorId, tenantId }),
    });
  } catch {
    // Silently ignore all errors
  }
}

export async function toggleDrugFavourite(drugId: string, doctorId: string): Promise<void> {
  await safeFetch<void>(`${BASE_URL}/drugs/${encodeURIComponent(drugId)}/favourite`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ doctorId }),
  });
}

export async function checkDrugInteractions(moleculeIds: string[]): Promise<any[]> {
  const response = await safeFetch<{ interactions: any[] }>(`${BASE_URL}/drugs/interactions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ moleculeIds }),
  });
  return response?.interactions || [];
}

// ------------------ FrontDesk Patient Page ------------------

export async function getPatientPrescriptions(
  patientId: string,
  options?: { from?: string; to?: string }
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options?.from) params.append('from', options.from);
  if (options?.to)   params.append('to', options.to);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await safeFetch<any[]>(
    `${BASE_URL}/prescriptions/patient/${patientId}${query}`
  );
  return Array.isArray(response) ? response : [];
}

export async function getPatientInvoices(
  patientId: string,
  options?: { from?: string; to?: string }
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options?.from) params.append('from', options.from);
  if (options?.to)   params.append('to', options.to);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await safeFetch<any[]>(
    `${BASE_URL}/billing/invoices/patient/${patientId}${query}`
  );
  if (Array.isArray(response)) return response;
  if (response && Array.isArray((response as any).invoices))
    return (response as any).invoices;
  return [];
}

export function getSixMonthsAgoDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toISOString().split('T')[0];
}